/**
 * Sentry integration for player-core error tracking and performance monitoring.
 *
 * Provides video-specific error handling, buffering analytics, and playback quality metrics.
 */

import * as Sentry from "@sentry/browser";

/** Configuration for player error tracking */
export type PlayerSentryConfig = {
  /** Asset ID for context */
  assetId?: string;
  /** Title ID for context */
  titleId?: string;
  /** User ID for context */
  userId?: string;
  /** Additional context tags */
  tags?: Record<string, string>;
};

/** Player-specific error types */
export type PlayerErrorType =
  | "hls_error"
  | "network_error"
  | "media_error"
  | "buffer_error"
  | "token_refresh_error"
  | "quality_switch_error"
  | "caption_error"
  | "playback_error";

/** Enhanced error context for player events */
export type PlayerErrorContext = {
  errorType: PlayerErrorType;
  assetId?: string;
  titleId?: string;
  position?: number;
  duration?: number;
  quality?: number | "auto";
  bufferHealth?: number;
  networkState?: number;
  readyState?: number;
  userAgent?: string;
  hlsVersion?: string;
  errorDetails?: Record<string, unknown>;
};

/** Player performance metrics */
export type PlayerMetrics = {
  /** Time to first frame (ms) */
  timeToFirstFrame?: number;
  /** Time to start playback (ms) */
  timeToPlay?: number;
  /** Buffer stalls count */
  bufferStalls?: number;
  /** Quality switches count */
  qualitySwitches?: number;
  /** Average bitrate */
  averageBitrate?: number;
  /** Dropped frames */
  droppedFrames?: number;
};

/**
 * Initialize Sentry context for player session
 */
export function setPlayerSentryContext(config: PlayerSentryConfig): void {
  Sentry.setContext("player", {
    assetId: config.assetId,
    titleId: config.titleId,
    userId: config.userId,
    timestamp: new Date().toISOString(),
  });

  // Set tags for filtering
  if (config.tags) {
    for (const [key, value] of Object.entries(config.tags)) {
      Sentry.setTag(key, value);
    }
  }

  // Set user context if provided
  if (config.userId) {
    Sentry.setUser({ id: config.userId });
  }
}

/**
 * Capture player error with enhanced context
 */
export function capturePlayerError(error: Error | string, context: PlayerErrorContext): void {
  Sentry.withScope((scope) => {
    // Set error type tag
    scope.setTag("error_type", context.errorType);
    scope.setTag("component", "player-core");

    // Set player context
    scope.setContext("player_error", {
      assetId: context.assetId,
      titleId: context.titleId,
      position: context.position,
      duration: context.duration,
      quality: context.quality,
      bufferHealth: context.bufferHealth,
      networkState: context.networkState,
      readyState: context.readyState,
      hlsVersion: context.hlsVersion,
      timestamp: new Date().toISOString(),
    });

    // Add error details if provided
    if (context.errorDetails) {
      scope.setContext("error_details", context.errorDetails);
    }

    // Set fingerprint for better error grouping
    const fingerprint = ["player-core", context.errorType, context.assetId || "unknown-asset"];
    scope.setFingerprint(fingerprint);

    // Capture the error
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(error, "error");
    }
  });
}

/**
 * Capture player performance metrics
 */
export function capturePlayerMetrics(metrics: PlayerMetrics, _context: { assetId?: string; titleId?: string }): void {
  // Track performance metrics
  if (metrics.timeToFirstFrame !== undefined) {
    Sentry.metrics.distribution("player.time_to_first_frame", metrics.timeToFirstFrame, {
      unit: "millisecond",
    });
  }

  if (metrics.timeToPlay !== undefined) {
    Sentry.metrics.distribution("player.time_to_play", metrics.timeToPlay, {
      unit: "millisecond",
    });
  }

  if (metrics.bufferStalls !== undefined) {
    Sentry.metrics.distribution("player.buffer_stalls", metrics.bufferStalls, {
      unit: "none",
    });
  }

  if (metrics.qualitySwitches !== undefined) {
    Sentry.metrics.distribution("player.quality_switches", metrics.qualitySwitches, {
      unit: "none",
    });
  }
}

/**
 * Track buffer health and stall events
 */
export function trackBufferHealth(video: HTMLVideoElement, context: { assetId?: string; titleId?: string }): void {
  try {
    const buffered = video.buffered;
    const currentTime = video.currentTime;

    if (buffered.length > 0) {
      let bufferAhead = 0;

      // Find buffer range containing current time
      for (let i = 0; i < buffered.length; i++) {
        const start = buffered.start(i);
        const end = buffered.end(i);

        if (currentTime >= start && currentTime <= end) {
          bufferAhead = end - currentTime;
          break;
        }
      }

      // Track buffer health metric
      Sentry.metrics.gauge("player.buffer_ahead", bufferAhead, {
        unit: "second",
      });

      // Warn about low buffer
      if (bufferAhead < 5 && bufferAhead > 0) {
        capturePlayerError("Low buffer warning", {
          errorType: "buffer_error",
          assetId: context.assetId,
          titleId: context.titleId,
          position: currentTime,
          duration: video.duration,
          bufferHealth: bufferAhead,
          networkState: video.networkState,
          readyState: video.readyState,
        });
      }
    }

    // Track network and ready states
    Sentry.metrics.gauge("player.network_state", video.networkState, {
      unit: "none",
    });

    Sentry.metrics.gauge("player.ready_state", video.readyState, {
      unit: "none",
    });
  } catch (error) {
    // Silently handle any errors in buffer tracking
    console.warn("Buffer health tracking error:", error);
  }
}

/** HLS Error structure from hls.js */
export type HlsError = {
  type?: string;
  details?: string;
  fatal?: boolean;
  [key: string]: unknown;
};

/**
 * Enhanced error handler for HLS.js errors
 */
export function handleHlsError(
  hlsError: HlsError,
  context: { assetId?: string; titleId?: string; video?: HTMLVideoElement },
): void {
  const errorType = String(hlsError.type || "unknown");
  const errorDetails = String(hlsError.details || "unknown");
  const fatal = Boolean(hlsError.fatal);

  // Determine error category
  let playerErrorType: PlayerErrorType = "hls_error";

  if (errorType.includes("NETWORK")) {
    playerErrorType = "network_error";
  } else if (errorType.includes("MEDIA")) {
    playerErrorType = "media_error";
  } else if (errorType.includes("BUFFER")) {
    playerErrorType = "buffer_error";
  }

  const errorContext: PlayerErrorContext = {
    errorType: playerErrorType,
    assetId: context.assetId,
    titleId: context.titleId,
    position: context.video?.currentTime,
    duration: context.video?.duration,
    networkState: context.video?.networkState,
    readyState: context.video?.readyState,
    errorDetails: {
      hlsErrorType: errorType,
      hlsErrorDetails: errorDetails,
      fatal,
      errorObject: hlsError,
    },
  };

  // Only capture fatal errors or specific non-fatal ones
  if (fatal || shouldCaptureHlsError(errorDetails)) {
    const errorMessage = `HLS ${errorType}: ${errorDetails}`;
    capturePlayerError(errorMessage, errorContext);
  }
}

/**
 * Determine if non-fatal HLS error should be captured
 */
function shouldCaptureHlsError(errorDetails: string): boolean {
  const significantErrors = [
    "MANIFEST_LOAD_ERROR",
    "MANIFEST_PARSING_ERROR",
    "LEVEL_LOAD_ERROR",
    "FRAG_LOAD_ERROR",
    "KEY_LOAD_ERROR",
    "BUFFER_STALLED_ERROR",
  ];

  return significantErrors.some((error) => errorDetails.includes(error));
}
